import { useCallback, useEffect, useMemo, useState } from "react";
import useDebouncedValue from "./useDebouncedValue";
import {
  EMPTY_FILTERS,
  applyFilters,
  applySort,
  countActiveFilters,
  deriveFilterOptions,
} from "../lib/tableFilters";
import {
  COLUMNS,
  DEFAULT_HIDDEN_COLUMNS,
} from "../components/results/ResultsTable";

/**
 * All the table's working state in one place: filters, sort, selection,
 * pagination and column visibility.
 *
 * Keeping it in a hook rather than inside the page component means the same
 * behaviour can be reused by the history detail view, and the page itself
 * stays readable as layout rather than logic.
 */
export default function useResultsTable(results = [], conflicts = []) {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [sort, setSort] = useState({ key: null, direction: "asc" });
  const [selected, setSelected] = useState(() => new Set());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [view, setView] = useState("list"); // "grid" | "list" | "table"
  const [visibleColumns, setVisibleColumns] = useState(() =>
    Object.fromEntries(
      COLUMNS.map((c) => [c.key, !DEFAULT_HIDDEN_COLUMNS.has(c.key)]),
    ),
  );

  // Text filters run on a short delay so typing stays smooth on large sets.
  const debouncedFilters = useDebouncedValue(filters, 180);

  const similarityMap = useMemo(
    () => new Map(conflicts.map((c) => [c.trademarkId, c.similarityScore])),
    [conflicts],
  );

  const reasoningMap = useMemo(
    () => new Map(conflicts.map((c) => [c.trademarkId, c.reasoning])),
    [conflicts],
  );

  const options = useMemo(() => deriveFilterOptions(results), [results]);

  const filteredRows = useMemo(
    () => applyFilters(results, debouncedFilters, similarityMap),
    [results, debouncedFilters, similarityMap],
  );

  const sortedRows = useMemo(
    () => applySort(filteredRows, sort, similarityMap),
    [filteredRows, sort, similarityMap],
  );

  const pageRows = useMemo(
    () => sortedRows.slice((page - 1) * pageSize, page * pageSize),
    [sortedRows, page, pageSize],
  );

  // A new search, or a filter change that shrinks the set, can strand the
  // user on a page that no longer exists.
  useEffect(() => {
    setPage(1);
  }, [debouncedFilters, pageSize, results]);

  useEffect(() => {
    setSelected(new Set());
    setFilters(EMPTY_FILTERS);
    setSort({ key: null, direction: "asc" });
  }, [results]);

  /** Clicking a header cycles ascending, descending, then unsorted. */
  const toggleSort = useCallback((key) => {
    setSort((current) => {
      if (current.key !== key) return { key, direction: "asc" };
      if (current.direction === "asc") return { key, direction: "desc" };
      return { key: null, direction: "asc" };
    });
  }, []);

  const toggleRow = useCallback((id) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAllOnPage = useCallback(() => {
    const ids = pageRows.map((r) => r.trademarkId);
    setSelected((current) => {
      const next = new Set(current);
      const allSelected = ids.length > 0 && ids.every((id) => next.has(id));
      ids.forEach((id) => (allSelected ? next.delete(id) : next.add(id)));
      return next;
    });
  }, [pageRows]);

  const toggleColumn = useCallback((key) => {
    setVisibleColumns((current) => ({ ...current, [key]: !current[key] }));
  }, []);

  const resetFilters = useCallback(() => setFilters(EMPTY_FILTERS), []);
  const clearSelection = useCallback(() => setSelected(new Set()), []);

  return {
    filters,
    setFilters,
    resetFilters,
    activeFilterCount: countActiveFilters(filters),
    options,
    sort,
    toggleSort,
    selected,
    toggleRow,
    toggleAllOnPage,
    clearSelection,
    page,
    setPage,
    pageSize,
    setPageSize,
    view,
    setView,
    visibleColumns,
    toggleColumn,
    similarityMap,
    reasoningMap,
    filteredRows: sortedRows,
    pageRows,
  };
}
